import React, { useCallback, useState, useEffect } from 'react';
// Libs
import Routes from 'Routes';
import AppConfig from 'appConfig';
import AppAuth from 'AppAuth';
import AppFetch from 'fetch/AppFetch';
import { getAppTitle, getModuleFromUrl, isZywaveUrl } from 'lib/AppHelpers';
import AppError from 'lib/AppError';
// TP Components
import Favicon from 'react-favicon';
import { Helmet } from 'react-helmet-async';
import ZuiHeader from 'components/Layout/ZuiHeader';
import ZuiNavigation from 'components/Layout/ZuiNavigation';
import ZuiFooter from 'components/Layout/ZuiFooter';
import { ErrorBoundary } from 'components/ErrorBoundary/ErrorBoundary';

export default function AppLayoutV2() {
  const [userInfo, setUserInfo] = useState(null);
  const [isLoadingUserInfo, setIsLoadingUserInfo] = useState(true);
  const module = getModuleFromUrl(window.location.href);
  const appTitle = getAppTitle({ module: module });

  // Load user info asynchronously
  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        if (AppAuth.loggedIn()) {
          const info = await AppFetch.fetchUserInfo();
          setUserInfo(info);
        }
      } catch (error) {
        console.error('Failed to load user info:', error);

        // Throw AppError for critical user info loading failures
        // This will be caught by the ErrorBoundary and show proper error UI
        const appError = new AppError(
          'Unable to load user information. Please refresh the page or contact support if the problem continues.',
          error?.statusCode || error?.status || 500,
          {
            originalError: error,
            component: 'AppLayoutV2',
            operation: 'loadUserInfo',
            timestamp: new Date().toISOString(),
          },
          `Failed to fetch user info: ${error?.message || 'Unknown error'}`
        );

        throw appError;
      } finally {
        setIsLoadingUserInfo(false);
      }
    };

    loadUserInfo();
  }, []);

  // Application error handler - throws AppError to be caught by ErrorBoundary
  const onAppError = useCallback((error) => {
    console.error('Application error:', error);

    // Create a structured AppError for the ErrorBoundary to handle
    const appError = new AppError(
      'An unexpected application error occurred. Please try refreshing the page or contact support if the problem continues.',
      error?.statusCode || error?.status || 500,
      {
        originalError: error,
        component: 'AppLayoutV2',
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      },
      error?.message || 'Unknown application error'
    );

    // Throw the AppError so it bubbles up to the ErrorBoundary
    throw appError;
  }, []);

  // set the favicon based on module
  const favIconUrl = `${process.env.PUBLIC_URL}/zywave-favicon.ico`;

  // Show loading state while user info is being fetched
  if (isLoadingUserInfo) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        Loading...
      </div>
    );
  }

  return (
    <ErrorBoundary>
      {isZywaveUrl() ? (
        <>
          <zywave-api-proxy api-base-url={AppConfig.zywaveProxyUrl}></zywave-api-proxy>
          <zywave-shell
            unresolved
            appName={appTitle}
            logout-user-url={AppConfig.logoutUrl || '/edge/logout'}
          >
            <zywave-shell-content unresolved>
              <Routes />
            </zywave-shell-content>
          </zywave-shell>
        </>
      ) : (
        <zui-shell unresolved>
          <Helmet>
            <title>{appTitle}</title>
          </Helmet>
          <Favicon url={favIconUrl} />
          <ZuiHeader module={module} />
          {userInfo?.products?.length > 0 && (
            <ZuiNavigation onAppError={onAppError} userInfo={userInfo} module={module} />
          )}
          <zui-shell-content slot="content">
            <Routes />
          </zui-shell-content>
          <ZuiFooter />
        </zui-shell>
      )}
    </ErrorBoundary>
  );
}
